import React from 'react';
import { Text, View, StyleSheet } from '@react-pdf/renderer';
import { BuildupStep, buildupLines, ProjectLevelSplit } from '../utils/internalSplitBuildup';

const TEXT_DARK = '#00273F';
const TEXT_GRAY = '#464646';
const BORDER_GRAY = '#E2E5EA';
const BG_LIGHT = '#F5F6F8';
const BRAND_RED = '#EB1C37';

const styles = StyleSheet.create({
  buildupBlock: { paddingLeft: 20, marginBottom: 3 },
  buildupRow: { flexDirection: 'row', marginBottom: 1 },
  buildupLabel: { width: 120, fontSize: 7, color: TEXT_GRAY },
  buildupValue: { flex: 1, fontSize: 7, color: TEXT_DARK },
  stepBlock: { paddingLeft: 20, marginTop: 2, marginBottom: 4 },
  stepRow: { flexDirection: 'row', marginBottom: 1 },
  stepRowEmphasis: {
    flexDirection: 'row',
    marginTop: 2,
    paddingTop: 2,
    borderTop: `1px solid ${BORDER_GRAY}`,
  },
  stepLabel: { width: 80, fontSize: 7, color: TEXT_GRAY },
  stepLabelEmphasis: { width: 80, fontSize: 7, fontFamily: 'Helvetica-Bold', color: TEXT_DARK },
  stepCalc: { flex: 1, fontSize: 7, color: '#969696' },
  stepValue: { width: 70, fontSize: 7, color: TEXT_DARK, textAlign: 'right' },
  stepValueEmphasis: {
    width: 70,
    fontSize: 7,
    fontFamily: 'Helvetica-Bold',
    color: TEXT_DARK,
    textAlign: 'right',
  },
  sectionHeading: {
    fontSize: 7.5,
    fontFamily: 'Helvetica-Bold',
    color: BRAND_RED,
    textTransform: 'uppercase',
    letterSpacing: 0.8,
    marginBottom: 6,
    paddingBottom: 3,
    borderBottom: `1px solid ${BRAND_RED}`,
  },
  summaryBox: {
    padding: 12,
    backgroundColor: BG_LIGHT,
    border: `1px solid ${BORDER_GRAY}`,
    marginBottom: 10,
  },
  groupLabel: {
    fontSize: 8,
    fontFamily: 'Helvetica-Bold',
    color: TEXT_DARK,
    marginBottom: 4,
  },
  lineRow: { flexDirection: 'row', marginBottom: 3, paddingLeft: 10 },
  lineLabel: { flex: 1, fontSize: 8, color: TEXT_GRAY },
  lineLabelBold: { flex: 1, fontSize: 8, fontFamily: 'Helvetica-Bold', color: TEXT_DARK },
  colMonthly: { width: 96, fontSize: 8, color: TEXT_DARK, textAlign: 'right' },
  colContract: { width: 96, fontSize: 8, color: TEXT_DARK, textAlign: 'right' },
  colHead: { fontSize: 7, fontFamily: 'Helvetica-Bold', color: TEXT_GRAY, textAlign: 'right' },
  noteRow: { flexDirection: 'row', marginBottom: 3, paddingLeft: 20 },
  note: { flex: 1, fontSize: 7, color: TEXT_GRAY },
  divider: { borderTop: `1px solid ${BORDER_GRAY}`, marginVertical: 5 },
});

const currency = (n: number): string =>
  `$${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// Renders the itemised cost-to-price build-up for an NRE or per-pipe line: one
// row per component, then the cost subtotal and the margin step to the price,
// with those two rows ruled off and bolded.
export const BuildupStepsTable: React.FC<{ steps: BuildupStep[] }> = ({ steps }) => (
  <View style={styles.stepBlock}>
    {steps.map((step, i) => {
      const emphasised = step.kind === 'cost' || step.kind === 'price';
      return (
        <View key={i} style={emphasised ? styles.stepRowEmphasis : styles.stepRow}>
          <Text style={emphasised ? styles.stepLabelEmphasis : styles.stepLabel}>{step.label}</Text>
          <Text style={styles.stepCalc}>{step.calc ?? ''}</Text>
          <Text style={emphasised ? styles.stepValueEmphasis : styles.stepValue}>{step.value}</Text>
        </View>
      );
    })}
  </View>
);

// Renders the cost-to-price build-up rows for a single priced line, drawn from
// the `detail` the calculators already carry. Nothing renders when the line has
// no recognised build-up (for example a flat per-tablet fee).
export const LineBuildup: React.FC<{ detail?: Record<string, unknown> }> = ({ detail }) => {
  const lines = buildupLines(detail);
  if (lines.length === 0) return null;
  return (
    <View style={styles.buildupBlock}>
      {lines.map((line, i) => (
        <View key={i} style={styles.buildupRow}>
          <Text style={styles.buildupLabel}>{line.label}</Text>
          <Text style={styles.buildupValue}>{line.value}</Text>
        </View>
      ))}
    </View>
  );
};

// The project-level recurring block: the two-part platform fee (base plus the
// per-module increment) and the post-handover retention tier with its totals.
export const ProjectLevelSection: React.FC<{ split: ProjectLevelSplit }> = ({ split }) => {
  const monthsLabel = split.months === 1 ? 'month' : 'months';
  const hasPlatform = split.platform.components.length > 0;
  return (
    <View wrap={false}>
      <Text style={styles.sectionHeading}>Project-Level Recurring: Platform & Retention</Text>
      <View style={styles.summaryBox}>
        {hasPlatform && (
          <>
            <Text style={styles.groupLabel}>
              Platform Subscription ({split.months} {monthsLabel})
            </Text>
            <View style={styles.lineRow}>
              <Text style={[styles.lineLabel, styles.colHead, { textAlign: 'left' }]} />
              <Text style={[styles.colMonthly, styles.colHead]}>Monthly</Text>
              <Text style={[styles.colContract, styles.colHead]}>Contract Total</Text>
            </View>
            {split.platform.components.map((c, i) => (
              <View key={i}>
                <View style={styles.lineRow}>
                  <Text style={styles.lineLabel}>{c.label}</Text>
                  <Text style={styles.colMonthly}>{currency(c.monthly)}</Text>
                  <Text style={styles.colContract}>{currency(c.contractTotal)}</Text>
                </View>
                {c.baseCost !== undefined && (
                  <>
                    {c.infraModuleCount !== undefined && (
                      <View style={styles.noteRow}>
                        <Text style={styles.note}>
                          Infra {currency(c.infraBaseline ?? 0)} baseline +{' '}
                          {currency(c.perModuleInfraMonthly ?? 0)}/mo x {Number(c.infraModuleCount ?? 0)} modules ={' '}
                          {currency(c.baseInfraMonthly ?? 0)}/mo infra
                        </Text>
                      </View>
                    )}
                    <View style={styles.noteRow}>
                      <Text style={styles.note}>
                        Infra {currency(c.baseInfraMonthly ?? 0)} + support{' '}
                        {Number(c.baseSupportHours ?? 0)} hrs x {currency(c.supportHourlyRate ?? 0)}/hr ={' '}
                        {currency(c.baseCost)}/mo cost
                      </Text>
                    </View>
                    <View style={styles.noteRow}>
                      <Text style={styles.note}>
                        Base fee = cost / (1 - {Math.round((c.marginTarget ?? 0) * 100)}% margin) ={' '}
                        {currency(c.baseFeeGross ?? 0)}/mo, before the duration discount
                      </Text>
                    </View>
                  </>
                )}
                {c.moduleCount !== undefined && c.perModuleMonthly !== undefined && (
                  <View style={styles.noteRow}>
                    <Text style={styles.note}>
                      {c.moduleCount} modules x {currency(c.perModuleMonthly)}/mo
                    </Text>
                  </View>
                )}
                {c.users !== undefined && c.perUserMonthly !== undefined && (
                  <View style={styles.noteRow}>
                    <Text style={styles.note}>
                      {c.users} users x {currency(c.perUserMonthly)}/mo
                    </Text>
                  </View>
                )}
              </View>
            ))}
            <View style={styles.lineRow}>
              <Text style={styles.lineLabelBold}>Platform Sub-Total</Text>
              <Text style={styles.colMonthly}>{currency(split.platform.monthly)}</Text>
              <Text style={styles.colContract}>{currency(split.platform.contractTotal)}</Text>
            </View>
          </>
        )}

        {split.retention && (
          <>
            {hasPlatform && <View style={styles.divider} />}
            <Text style={styles.groupLabel}>Data Retention (post-handover)</Text>
            <View style={styles.lineRow}>
              <Text style={styles.lineLabel}>
                {split.retention.tier} tier: {currency(split.retention.annual)}/yr x{' '}
                {split.retention.years} yrs
              </Text>
              <Text style={styles.colMonthly} />
              <Text style={styles.colContract}>{currency(split.retention.contractTotal)}</Text>
            </View>
          </>
        )}
      </View>
    </View>
  );
};
